import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Field, useAsync } from '../components/ui';
import type { Graph, Step, VariableSource } from '../journeys/model';
import { api } from '../lib/api';
import { ChannelChip } from './components';
import { ChannelTemplate, eur, PolicyCheck, PolicyRow } from './model';
import { t } from '../lib/i18n';

type NotifyStep = Extract<Step, { type: 'send_notification' }>;

const PATHS = ['contact.email', 'contact.external_id', 'context.properties.', 'waited_event.properties.'];

/**
 * The panel of a «Уведомление» step (BUILD-SPEC §5.4): a channel template and a cascade policy with pinned versions,
 * the mapping of template variables, the stream, what to do when every channel is skipped, and the worst-case price.
 */
export function SendNotificationForm({ graph, step, setStep, disabled, next, attributeKeys }: { graph: Graph; step: NotifyStep; setStep: (fn: (s: Step) => void) => void; disabled: boolean; next: ReactNode; attributeKeys: string[] }) {
  const policies = useAsync(() => api.get<{ items: PolicyRow[]; builtins: PolicyRow[] }>('/cascade-policies'));
  const templates = useAsync(() => api.get<{ items: ChannelTemplate[] }>('/channel-templates'));
  const tpl = templates.data?.items.find((t) => t.key === step.template.key);
  const tplFull = useAsync(() => (step.template.key ? api.get<ChannelTemplate>(`/channel-templates/${step.template.key}`) : Promise.resolve(null)), [step.template.key]);
  const pol = [...(policies.data?.items ?? []), ...(policies.data?.builtins ?? [])].find((p) => p.key === step.policy.key);
  const versions = useAsync(() => (pol?.version ? api.get<{ items: PolicyRow[] }>(`/cascade-policies/${pol.key}/versions`) : Promise.resolve({ items: [] as PolicyRow[] })), [pol?.key, pol?.version]);
  const pinnedPolicy = typeof step.policy.version === 'number' ? versions.data?.items.find((v) => v.version === step.policy.version) : null;
  const def = pinnedPolicy?.definition ?? pol?.definition;
  const price = useAsync(() => (def ? api.post<PolicyCheck>('/cascade-policies/validate', { definition: def }).catch(() => null) : Promise.resolve(null)), [JSON.stringify(def)]);
  const stream = step.stream ?? graph.delivery.stream;
  const vars = useMemo(() => (tpl?.variables ?? []).filter((v) => v !== 'code'), [tpl]);
  const usable = (policies.data ? [...policies.data.items, ...policies.data.builtins] : []).filter((p) => p.definition.kind === 'message');
  const setN = (fn: (s: NotifyStep) => void) => setStep((s) => fn(s as NotifyStep));

  function pickTemplate(key: string) {
    const t = templates.data?.items.find((x) => x.key === key);
    setN((s) => {
      s.template = { key, version: 'latest' };
      for (const v of t?.variables ?? []) if (v !== 'code' && !(v in s.variables)) s.variables[v] = { path: `contact.attributes.${v}` };
    });
  }
  return (
    <>
      <Field label={t('Политика каскада')} hint={<>{t('Только «сообщение»: коды отправляет Verify API.')} <Link to="/channels/policies">{t('Политики')}</Link></>}>
        <select className="select input" value={step.policy.key} onChange={(e) => setN((s) => { s.policy = { key: e.target.value, version: 'latest' }; })} disabled={disabled}>
          <option value="">{t('— выберите —')}</option>
          {usable.map((p) => <option key={p.key} value={p.key}>{p.key}{p.version ? ` (v${p.version})` : t(' (встроенная)')}</option>)}
          {step.policy.key && !pol && <option value={step.policy.key}>{t('{key} (не найдена)', { key: step.policy.key })}</option>}
        </select>
      </Field>
      {pol && pol.version > 0 && (
        <Field label={t('Версия политики')} hint={t('Закрепляется в версии сценария: изменение политики потом не меняет одобренный сценарий')}>
          <select className="select input" value={typeof step.policy.version === 'number' ? step.policy.version : 'latest'} onChange={(e) => setN((s) => { s.policy = { key: pol.key, version: e.target.value === 'latest' ? 'latest' : Number(e.target.value) }; })} disabled={disabled}>
            <option value="latest">{t('последняя (сейчас v{version})', { version: pol.version })}</option>
            {(versions.data?.items ?? []).map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
          </select>
        </Field>
      )}
      {def && (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row wrap" style={{ gap: 4 }}>
            {def.steps.map((s, i) => <span key={i} className="row" style={{ gap: 4 }}>{i > 0 && <span className="muted">→</span>}<ChannelChip channel={s.channel} /></span>)}
          </div>
          <div className="small">{t('Худший случай:')} <b>{price.data ? eur(price.data.estimated_cost_max.amount) : '…'}</b> {t('на участника')}{price.data ? ` (${price.data.country})` : ''}. {t('Обычно дешевле — каскад останавливается на успехе.')}</div>
          {stream === 'marketing' && def.on_timeout === 'continue' && <div className="callout bad small"><div>{t('Маркетинговому уведомлению нужна политика с «если нет сигнала — остановиться»: иначе человек может получить сообщение дважды. Сохранение версии сценария это не пропустит.')}</div></div>}
        </div>
      )}
      <Field label={t('Шаблон канала')} hint={templates.data && !templates.data.items.length ? t('Шаблонов нет — создайте в «Каналы → Шаблоны»') : undefined}>
        <select className="select input" value={step.template.key} onChange={(e) => pickTemplate(e.target.value)} disabled={disabled}>
          <option value="">{t('— выберите —')}</option>
          {(templates.data?.items ?? []).map((t) => <option key={t.key} value={t.key}>{t.name} · {t.key}</option>)}
          {step.template.key && !tpl && <option value={step.template.key}>{t('{key} (не найден)', { key: step.template.key })}</option>}
        </select>
      </Field>
      {tpl && (
        <>
          <Field label={t('Версия шаблона')}>
            <select className="select input" value={typeof step.template.version === 'number' ? step.template.version : 'latest'} onChange={(e) => setN((s) => { s.template = { key: tpl.key, version: e.target.value === 'latest' ? 'latest' : Number(e.target.value) }; })} disabled={disabled}>
              <option value="latest">{t('последняя (сейчас v{version})', { version: tpl.version })}</option>
              {(tplFull.data?.versions ?? []).map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
            </select>
          </Field>
          <div className="row wrap" style={{ gap: 4 }}>{tpl.channels.map((c) => <ChannelChip key={c} channel={c} />)}<span className="small muted">{t('— варианты шаблона; шаг без варианта пропускается')}</span></div>
        </>
      )}
      {vars.length > 0 && (
        <Field label={t('Переменные шаблона')} hint={t('Путь — начинается с «=»: =contact.attributes.first_name; иначе — постоянное значение')}>
          <div className="stack" style={{ gap: 6 }}>
            {vars.map((name) => {
              const src = step.variables[name] as VariableSource | undefined;
              const isPath = src !== undefined && typeof src === 'object' && src !== null;
              const text = src === undefined ? '' : isPath ? `=${(src as { path: string }).path}` : String(src);
              return (
                <div key={name} className="kv-row">
                  <code style={{ overflowWrap: 'anywhere' }}>{name}</code>
                  <input
                    className="input input-mono"
                    list="notify-paths"
                    value={text}
                    placeholder="=contact.attributes.…"
                    onChange={(e) => setN((s) => {
                      const raw = e.target.value;
                      if (!raw) delete s.variables[name];
                      else if (raw.startsWith('=')) s.variables[name] = { path: raw.slice(1).trim(), ...(isPath && (src as { default?: unknown }).default !== undefined ? { default: (src as { default: string }).default } : {}) };
                      else s.variables[name] = raw;
                    })}
                    disabled={disabled}
                    aria-label={t('Источник {name}', { name })}
                  />
                  {isPath ? (
                    <input className="input" style={{ width: 96 }} placeholder={t('по умолч.')} value={String((src as { default?: unknown }).default ?? '')} onChange={(e) => setN((s) => {
                      const v = s.variables[name] as { path: string; default?: string };
                      if (e.target.value) v.default = e.target.value;
                      else delete v.default;
                    })} disabled={disabled} aria-label={t('Значение по умолчанию {name}', { name })} />
                  ) : <span />}
                </div>
              );
            })}
            <datalist id="notify-paths">{[...attributeKeys.map((k) => `=contact.attributes.${k}`), ...PATHS.map((p) => `=${p}`)].map((p) => <option key={p} value={p} />)}</datalist>
          </div>
        </Field>
      )}
      <Field label={t('Поток')} hint={stream === 'marketing' ? t('Маркетинг: нужно согласие на канал, тихие часы и частотный лимит') : t('Сервисное: без согласия на маркетинг; согласующий подтверждает, что рекламы нет')}>
        <select className="select input" value={step.stream ?? ''} onChange={(e) => setN((s) => { if (e.target.value) s.stream = e.target.value as 'marketing'; else delete s.stream; })} disabled={disabled}>
          <option value="">{t('как у сценария ({kind})', { kind: graph.delivery.stream === 'marketing' ? t('маркетинг') : t('транзакционные') })}</option>
          <option value="marketing">{t('маркетинг')}</option>
          <option value="transactional">{t('транзакционные (сервисные)')}</option>
        </select>
      </Field>
      <Field label={t('Если все каналы пропущены')} hint={t('Нет адреса, согласия, подавление, тихие часы, лимит')}>
        <select className="select input" value={step.on_skip ?? 'continue'} onChange={(e) => setN((s) => { s.on_skip = e.target.value as 'continue' | 'exit'; })} disabled={disabled}>
          <option value="continue">{t('идти дальше')}</option>
          <option value="exit">{t('завершить участие')}</option>
        </select>
      </Field>
      {next}
    </>
  );
}
